<template>
  <div id="app">
    <div id="map"></div>
    <div id="loading">Loading Map...</div>
    <div class="header-info">
      <h1 class="text-lg font-bold mb-1">{{ expeditionInfo.title }}</h1>
      <p class="text-sm">{{ expeditionInfo.dates }}</p>
      <p class="text-xs">Aboard the {{ expeditionInfo.vessel }}</p>
    </div>
    <div id="content-container">
      <VoyageStop
        v-for="key in displayableLocationKeys"
        :key="key"
        :location-key="key"
        :title="key"
        :date="getLocationDate(key)"
        :description="getLocationDescription(key)"
        :photo-count="locations[key]?.photoCount || 1"
        :photos="locations[key]?.photos || []"
        :is-active="activeLocationKey === key"
        :is-minimized="isMobileView && !minimizedCards.has(key)"
        @card-click="toggleCard(key)"
        ref="voyageStopsRefs"
      />
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, computed, watch } from 'vue';
import VoyageStop from './components/VoyageStop.vue';
import { locations, locationOrder, expeditionInfo, intermediateLocations } from './config/voyageData';
import L from 'leaflet';
import 'leaflet-curve';
import 'leaflet.animatedmarker/src/AnimatedMarker.js';

export default {
  name: 'App',
  components: {
    VoyageStop
  },
  setup() {
    // Replace the hardcoded locations and locationOrder with the imported ones
    const locationsRef = ref(locations);
    const locationOrderRef = ref(locationOrder);
    const activeLocationKey = ref(null);

    // Add these new refs
    const isMobileView = ref(window.innerWidth <= 768);
    const minimizedCards = ref(new Set());

    // Add this new method
    const toggleCard = (key) => {
      if (isMobileView.value) {
        if (minimizedCards.value.has(key)) {
          minimizedCards.value.delete(key);
        } else {
          minimizedCards.value.add(key);
        }
      }
    };

    // Leaflet instances stored non-reactively
    let mapInstance = null;
    let mapMarkers = {};
    let routeLayer = null;
    let shipMarkerInstance = null;
    let observer = null;
    const voyageStopsRefs = ref([]);

    const routeLatLngs = computed(() => {
      return locationOrderRef.value.map(key => {
        const loc = locationsRef.value[key] || null;
        return loc ? [loc.lat, loc.lng] : null;
      }).filter(coords => coords !== null);
    });

    const displayableLocationKeys = computed(() => {
      return locationOrderRef.value.filter(key => locationsRef.value[key]?.description);
    });

    const createCustomIcon = () => {
      return L.divIcon({
        html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="#FF4444" stroke="white" stroke-width="1">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
        </svg>`,
        className: 'location-marker',
        iconSize: [24, 24],
        iconAnchor: [12, 24],
        popupAnchor: [0, -24]
      });
    };

    const initMap = () => {
      console.log("Vue: Initializing map...");
      const loadingIndicator = document.getElementById('loading');
      if (loadingIndicator) loadingIndicator.style.display = 'block';

      // Make sure Leaflet is loaded before proceeding
      if (!L || !L.map) {
        console.error("Vue: Leaflet library not loaded!");
        return;
      }

      try {
        // Store map instance directly
        mapInstance = L.map('map', {
          scrollWheelZoom: true,
          zoomControl: false,  // Disable default zoom control
          minZoom: 5
        });

        // Add zoom control to the right side
        L.control.zoom({
          position: 'bottomright'
        }).addTo(mapInstance);

        console.log("Vue: Map instance created:", mapInstance);

        mapInstance.setView([-65, -64], 5);

        L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
          attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
          maxZoom: 17, minZoom: 5
        }).addTo(mapInstance);

        mapInstance.setMaxBounds([[-50, -85], [-72, -55]]);

        // Draw route first, then add markers
        drawRoute();

        // Add markers on top of route
        mapMarkers = {};
        displayableLocationKeys.value.forEach(key => {
          const loc = locationsRef.value[key];
          if (loc) {
            const title = formatTitle(key);
            const marker = L.marker([loc.lat, loc.lng], {
              icon: createCustomIcon()
            })
              .addTo(mapInstance)
              .bindPopup(`<b>${title}</b>`);
            marker.on('click', () => handleMarkerClick(key));
            mapMarkers[key] = marker;
          }
        });
        console.log("Vue: Markers added:", mapMarkers);

        // Add animated ship last so it's on top
        addAnimatedShip();

        // Initial map focus
        setTimeout(() => {
          if (loadingIndicator) loadingIndicator.style.display = 'none';
          const firstLocationKey = displayableLocationKeys.value[0];
          if (firstLocationKey) {
            console.log("Vue: Flying to initial location:", firstLocationKey);
            flyToLocation(firstLocationKey);
            // Open popup for the first location initially
            if (mapMarkers[firstLocationKey]) {
              mapMarkers[firstLocationKey].openPopup();
            }
            // Set the first location as active initially
            activeLocationKey.value = firstLocationKey;

          } else {
            console.error("Vue: No displayable first location found.");
          }
        }, 1500);

      } catch (error) {
        console.error("Vue: Error initializing Leaflet map:", error);
        if (loadingIndicator) loadingIndicator.innerText = 'Error loading map.';
      }
    };

    const drawRoute = () => {
      if (!mapInstance) {
        console.error("Vue: Map instance not available for drawing route.");
        return;
      }
      
      if (routeLayer) {
        mapInstance.removeLayer(routeLayer);
        routeLayer = null;
      }
    
      // Use regular polyline for reliability
      routeLayer = L.polyline(routeLatLngs.value, {
        color: '#00ffff', // Bright cyan
        weight: 2.5,
        opacity: 0.8,
        dashArray: '5, 10', // Creates dashed line effect
        smoothFactor: 1
      }).addTo(mapInstance);
    
      console.log("Vue: Route path drawn with coordinates:", routeLatLngs.value);
    };

    const addAnimatedShip = () => {
      // Ensure map instance exists
      if (!mapInstance) {
        console.error("Vue: Map instance not available for adding ship.");
        return;
      }
      // Remove old marker first
      if (shipMarkerInstance) {
        mapInstance.removeLayer(shipMarkerInstance);
        shipMarkerInstance = null;
        console.log("Vue: Removed previous ship marker.");
      }

      console.log("Vue: Adding animated ship with path:", routeLatLngs.value);

      // Instead of using L.AnimatedMarker, use a regular marker
      const shipSVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 400" width="80" height="40">
        <!-- Main Hull -->
        <path fill="#E5E7EB" d="M700 250H100L40 100h720z" filter="url(#shadow)"/>
        <path fill="#F3F4F6" d="M700 250H100l-15-120h630z"/>
        
        <!-- Hull Details -->
        <path fill="#D1D5DB" d="M670 250H130l-8-60h556z"/>
        <path fill="#9CA3AF" d="M660 250H140l-5-30h530z"/>
        
        <!-- Bridge Structure (Moved back) -->
        <path fill="#F3F4F6" d="M500 180H300v-70h200z"/>
        <path fill="#E5E7EB" d="M500 150H300v-40h200z"/>
        
        <!-- Bridge Windows -->
        <rect fill="#60A5FA" x="320" y="130" width="25" height="15" rx="2"/>
        <rect fill="#60A5FA" x="355" y="130" width="25" height="15" rx="2"/>
        <rect fill="#60A5FA" x="390" y="130" width="25" height="15" rx="2"/>
        <rect fill="#60A5FA" x="425" y="130" width="25" height="15" rx="2"/>
        <rect fill="#60A5FA" x="460" y="130" width="25" height="15" rx="2"/>
        
        <!-- Upper Deck Details -->
        <path fill="#F3F4F6" d="M480 110H320l-15-30h190z"/>
        <path fill="#D1D5DB" d="M470 100H330l-10-15h160z"/>
        
        <!-- Navigation Lights -->
        <circle fill="#EF4444" cx="150" cy="220" r="4"/> <!-- Port Light -->
        <circle fill="#10B981" cx="650" cy="220" r="4"/> <!-- Starboard Light -->
        <circle fill="#FFFFFF" cx="400" cy="90" r="4"/> <!-- Masthead Light -->
        
        <!-- Radar/Communication Equipment -->
        <path fill="#9CA3AF" d="M380 80c0-11 20-20 20-20s20 9 20 20z"/>
        
        <!-- Ice Strengthened Bow -->
        <path fill="#F9FAFB" d="M100 250L40 150c30-15 60 0 60 0z"/>
        
        <!-- Wake Effect -->
        <path fill="#FFFFFF" opacity="0.6" d="M60 200c-5-5-7-12-3-18 13 3 33 8 49 8-16-8-33-25-41-41 16 8 49 16 74 13-16-8-33-25-41-49 25 16 57 25 82 16z"/>
        
        <!-- Filters -->
        <defs>
          <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="4" stdDeviation="4" flood-opacity="0.3"/>
          </filter>
        </defs>
      </svg>`;

      const shipIcon = L.divIcon({ 
        html: shipSVG, 
        className: 'ship-icon', 
        iconSize: [80, 40], // Adjusted to be more rectangular
        iconAnchor: [40, 20] // Center anchor point
      });

      // Create a regular marker instead of animated marker
      shipMarkerInstance = L.marker(routeLatLngs.value[0], {
        icon: shipIcon
      });
      mapInstance.addLayer(shipMarkerInstance);

      // Set initial position
      updateShipPosition(0);
    };

    const setupScrollInteraction = () => {
      console.log("Vue: Setting up scroll interaction...");
      
      const container = document.getElementById('content-container');
      if (!container) {
        console.error("Vue: Content container not found");
        return;
      }
    
      // Add scroll event listener for ship movement
      container.addEventListener('scroll', () => {
        const scrollHeight = container.scrollHeight - container.clientHeight;
        const scrollProgress = container.scrollTop / scrollHeight;
        updateShipPosition(scrollProgress);
      });
    
      // Enhanced intersection observer setup
      setTimeout(() => {
        const sections = voyageStopsRefs.value;
        if (!sections || sections.length === 0) {
          console.warn("Vue: No voyage stop sections found for observer.");
          return;
        }
        
        const options = {
          root: container, // Set container as root
          rootMargin: '-40% 0px -40% 0px', // Adjusted margins
          threshold: [0.5] // Simplified threshold
        };
      
        try {
          observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
              const locationId = entry.target.getAttribute('data-location');
              
              if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
                if (activeLocationKey.value !== locationId) {
                  console.log(`Vue: Section ${locationId} is most visible`);
                  activeLocationKey.value = locationId;
                  
                  const loc = locationsRef.value[locationId];
                  if (loc && mapInstance) {
                    const targetZoom = loc.zoom || 8;
                    console.log(`Vue: Flying to ${locationId} with zoom ${targetZoom}`);
                    
                    mapInstance.flyTo(
                      [loc.lat, loc.lng],
                      targetZoom,
                      {
                        animate: true,
                        duration: 1.5,
                        easeLinearity: 0.25
                      }
                    );
                  }
                }
              }
            });
          }, options);
      
          sections.forEach(section => {
            if (section.$el) { // Use $el to get the DOM element
              observer.observe(section.$el);
              console.log("Vue: Observing section:", section.$el.getAttribute('data-location'));
            }
          });
        } catch (error) {
          console.error("Vue: Error setting up IntersectionObserver:", error);
        }
      }, 500); // Increased delay to ensure refs are available
    };

    const addScrollStyles = () => {
      const style = document.createElement('style');
      style.textContent = `
        .voyage-stop {
          scroll-margin-top: 40vh;
          min-height: 60vh;
          margin-bottom: 20vh;
          scroll-snap-align: start;
          scroll-snap-stop: always;
        }
        
        #content-container {
          scroll-snap-type: y mandatory;
          padding-top: 20vh;
          padding-bottom: 20vh;
          height: 100vh;
          overflow-y: scroll;
          scroll-behavior: smooth;
        }
      `;
      document.head.appendChild(style);
    };

    const flyToLocation = (key) => {
      if (!key || !locationsRef.value[key] || !mapInstance) {
        console.warn(`Vue: flyToLocation called with invalid key or missing map: ${key}`);
        return;
      }
      const loc = locationsRef.value[key];
      const targetZoom = loc.zoom || 8;
      
      console.log(`Vue: Flying to ${key} with zoom ${targetZoom}`);
      
      // Stop any ongoing animations
      mapInstance.stop();
      
      mapInstance.flyTo(
        [loc.lat, loc.lng],
        targetZoom,
        {
          animate: true,
          duration: 1.5,
          easeLinearity: 0.25
        }
      );
    
      // Ensure marker popup is shown
      if (mapMarkers[key]) {
        mapMarkers[key].openPopup();
      }
    };

    const handleMarkerClick = (key) => {
      if (!key || !locationsRef.value[key] || !mapInstance) return;
      const loc = locationsRef.value[key];
      const targetZoom = loc.zoom || 8; // Use default zoom if not specified
      console.log(`Vue: Marker clicked for ${key}, flying to zoom level ${targetZoom}`);
      mapInstance.flyTo([loc.lat, loc.lng], targetZoom, {
        animate: true,
        duration: 0.5
      });
      // Also set the clicked location as active for highlighting
      activeLocationKey.value = key;
    };

    const formatTitle = (key) => {
      return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    const getLocationDate = (key) => {
      return locationsRef.value[key]?.date || '';
    };

    const getLocationDescription = (key) => {
      return locationsRef.value[key]?.description || '';
    };

    const getLocationPhotos = (key) => {
      const photos = locationsRef.value[key]?.photos || [];
      console.log(`Photos for ${key}:`, photos);
      return photos;
    };

    const updateShipPosition = (scrollProgress) => {
      if (!shipMarkerInstance || !routeLatLngs.value.length) return;

      // Calculate the index in the path based on scroll progress
      const pathLength = routeLatLngs.value.length - 1;
      const exactIndex = (pathLength * scrollProgress);
      const index1 = Math.floor(exactIndex);
      const index2 = Math.min(index1 + 1, pathLength);
      const remainder = exactIndex - index1;

      // Interpolate between two points
      const pos1 = routeLatLngs.value[index1];
      const pos2 = routeLatLngs.value[index2];
      const lat = pos1[0] + (pos2[0] - pos1[0]) * remainder;
      const lng = pos1[1] + (pos2[1] - pos1[1]) * remainder;

      // Update ship position
      shipMarkerInstance.setLatLng([lat, lng]);
    };

    onMounted(() => {
      console.log("Vue: Component mounted hook.");
      addScrollStyles();
      initMap();
      setupScrollInteraction();
      displayableLocationKeys.value.forEach(key => {
        console.log(`Location ${key} data:`, {
          photos: locationsRef.value[key]?.photos,
          photoCount: locationsRef.value[key]?.photoCount
        });
      });
      window.addEventListener('resize', () => {
        isMobileView.value = window.innerWidth <= 768;
      });
    });

    onBeforeUnmount(() => {
      console.log("Vue: Component beforeUnmount hook.");
      if (observer) {
        observer.disconnect();
        console.log("Vue: IntersectionObserver disconnected.");
      }
      if (mapInstance) {
        mapInstance.remove();
        console.log("Vue: Map instance removed.");
      }
      window.removeEventListener('resize', () => {
        isMobileView.value = window.innerWidth <= 768;
      });
    });

    // Watcher for activeLocationKey
    watch(activeLocationKey, (newKey, oldKey) => {
      if (!newKey || !locationsRef.value[newKey]) return;
      
      console.log(`Vue: Location changed from ${oldKey} to ${newKey}`);
      
      // Handle marker popups
      if (oldKey && mapMarkers[oldKey]) {
        mapMarkers[oldKey].closePopup();
      }
      if (mapMarkers[newKey]) {
        mapMarkers[newKey].openPopup();
      }
      
      // Get location data
      const loc = locationsRef.value[newKey];
      
      // Always use the specified zoom level for the location
      if (loc && mapInstance) {
        mapInstance.flyTo([loc.lat, loc.lng], loc.zoom || 8, {
          animate: true,
          duration: 1.5,
          easeLinearity: 0.25
        });
      }
    });

    watch(routeLatLngs, () => {
      console.log("Vue: routeLatLngs changed, redrawing route.");
      drawRoute();
    });

    return {
      locations: locationsRef,
      locationOrder: locationOrderRef,
      activeLocationKey,
      displayableLocationKeys,
      voyageStopsRefs,
      formatTitle,
      getLocationDate,
      getLocationDescription,
      expeditionInfo,
      getLocationPhotos,
      isMobileView,
      minimizedCards,
      toggleCard
    };
  }
};
</script>

<style scoped>
/* Reset body and app styles */
body, html {
  margin: 0;
  padding: 0;
  height: 100%;
  overflow: hidden;
}

#app {
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
}

#map {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  z-index: 0;
}

/* Fixed content container positioning with hidden scrollbar */
#content-container {
  position: fixed;
  top: 0;
  transform: translateX(-15%);
  width: 100vw;
  height: 100vh;
  padding-top: clamp(120px, 15vh, 150px); /* Add padding to account for header */
  margin: 0;
  overflow-y: scroll;
  overflow-x: hidden;
  z-index: 10;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE/Edge */
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* Hide scrollbar for Chrome/Safari */
#content-container::-webkit-scrollbar {
  display: none;
}

/* Content wrapper adjustments */
#content-container > * {
  width: 600px;
  margin-left: -20%;
  padding: 1rem;
  pointer-events: auto; /* Re-enable interactions for cards */
}

/* Content padding wrapper with responsive padding */
/* #content-container > * {
  width: 100%;
  padding-left: clamp(1rem, 5vw, 2rem);
  padding-right: clamp(1rem, 5vw, 2rem);
  box-sizing: border-box;
} */

/* Update header positioning */
.header-info {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 20;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(12px);
  padding: 0 1rem; /* Remove top/bottom padding, keep left/right */
  height: 100px;
  text-align: center;
  box-shadow: 
    0 0 0 1px rgba(255, 255, 255, 0.1),
    0 8px 24px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  justify-content: center;
  animation: fadeInTop 1s ease-out forwards;
}

.header-info::before {
  content: '';
  position: absolute;
  inset: -2px;
  border-radius: 1.6rem;
  background: linear-gradient(
    135deg,
    rgba(56, 189, 248, 0.5),  /* Sky blue */
    rgba(59, 130, 246, 0.5),  /* Blue */
    rgba(99, 102, 241, 0.5)   /* Indigo */
  );
  z-index: -1;
  opacity: 0.6;
}

.header-info h1 {
  font-size: 1.125rem; /* text-lg */
  font-weight: 700;
  margin-bottom: 0.25rem;
  line-height: 1.2;
  letter-spacing: -0.01em;
}

.header-info p {
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.875rem; /* text-sm */
  margin: 0.25rem;
  line-height: 1.2;
}

.header-info p:last-child {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.75rem; /* text-xs */
  margin-bottom: 0;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translate(-50%, -40%);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%);
  }
}

/* Enhanced voyage stop styling with mobile improvements */
.voyage-stop {
  background: linear-gradient(165deg, rgba(255, 255, 255, 0.98), rgba(255, 255, 255, 0.92));
  border: none;
  border-radius: clamp(0.75rem, 3vw, 1.25rem);
  box-shadow: 
    0 8px 16px rgba(0, 0, 0, 0.1),
    0 2px 4px rgba(0, 0, 0, 0.08),
    inset 0 0 0 1px rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  opacity: 0.95;
  overflow: visible;
  margin-bottom: clamp(20vh, 30vh, 40vh);
  padding: clamp(1.5rem, 5vw, 2.5rem);
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  transform: translateY(0);
  scroll-margin-top: calc(clamp(120px, 15vh, 150px) + 2rem); /* Account for header height */
  min-height: clamp(30vh, 35vh, 40vh);
  position: relative;
}

.voyage-stop::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 8px;
  background: linear-gradient(90deg, #3B82F6, #2563EB);
  border-radius: 1.25rem 1.25rem 0 0;
  opacity: 0;
  transition: opacity 0.4s ease;
}

.voyage-stop.active {
  background: linear-gradient(165deg, rgba(255, 255, 255, 0.99), rgba(255, 255, 255, 0.96));
  transform: translateY(-6px);
  box-shadow: 
    0 20px 25px rgba(0, 0, 0, 0.1),
    0 10px 10px rgba(0, 0, 0, 0.04),
    inset 0 0 0 1px rgba(255, 255, 255, 0.9);
  opacity: 1;
}

.voyage-stop.active::before {
  opacity: 1;
}

/* Location title styling with responsive font size */
.voyage-stop h2 {
  color: #1a365d;
  font-size: clamp(1.5rem, 4vw, 2rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  margin-bottom: 1rem;
  line-height: 1.2;
  background: linear-gradient(135deg, #1a365d, #2563EB);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* Date styling with responsive spacing */
.voyage-stop .text-gray-600 {
  color: #4a5568;
  font-size: clamp(0.75rem, 2vw, 0.875rem);
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  margin-bottom: clamp(1rem, 3vw, 1.5rem);
  opacity: 0.9;
  position: relative;
  padding-left: 1rem;
}

.voyage-stop .text-gray-600::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  width: 4px;
  height: 4px;
  background: #3B82F6;
  border-radius: 50%;
  transform: translateY(-50%);
}

/* Description styling with responsive font size */
.voyage-stop p:not(.text-gray-600) {
  color: #2d3748;
  line-height: 1.6;
  margin-bottom: clamp(1.5rem, 4vw, 2rem);
  font-size: clamp(1rem, 2.5vw, 1.125rem);
  font-weight: 400;
}

/* Enhanced photo container with responsive grid */
.photos-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: clamp(0.75rem, 2vw, 1.25rem);
  margin-top: clamp(1.5rem, 4vw, 2rem);
  padding-top: clamp(1.5rem, 4vw, 2rem);
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.photo-placeholder {
  aspect-ratio: 1;
  background: linear-gradient(135deg, #f7fafc, #edf2f7);
  border: 2px dashed #cbd5e0;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #4a5568;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.3s ease;
  overflow: hidden;
  position: relative;
}

.photo-placeholder::after {
  content: '+';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 2rem;
  color: #a0aec0;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.photo-placeholder:hover {
  border-color: #3B82F6;
  background: linear-gradient(135deg, #ebf8ff, #e6fffa);
  transform: translateY(-2px) scale(1.02);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.photo-placeholder:hover::after {
  opacity: 1;
}

/* Enhanced header info with responsive styling */
.header-info {
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.5);
  padding: clamp(1.5rem, 4vw, 2.5rem);
  border-radius: clamp(0.75rem, 3vw, 1.25rem);
  margin-bottom: clamp(2rem, 6vw, 4rem);
  text-align: center;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
}

.header-info h1 {
  font-size: clamp(1.75rem, 5vw, 2.5rem);
  font-weight: 800;
  letter-spacing: -0.025em;
  margin-bottom: 0;
  margin-top: 0.25rem;
  background: linear-gradient(135deg, #ffffff, #e2e8f0);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.photos-container img {
  height: 120px;
  width: 120px;
  object-fit: cover;
  border-radius: 0.375rem;
}

#loading {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 1rem 2rem;
  border-radius: 0.5rem;
  z-index: 1000;
  display: none;
}

.header-info {
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 0 1rem;
  margin-bottom: 3rem;
  text-align: center;
  opacity: 1;
  max-height: 1000px;
}

.ship-icon {
  transition: transform 0.1s ease-out;
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.5)) 
          drop-shadow(0 0 12px rgba(255,255,255,0.5))
          drop-shadow(0 0 20px rgba(96,165,250,0.5));
  background: radial-gradient(circle at center, rgba(255,255,255,0.3) 0%, transparent 70%);
  border-radius: 50%;
}

.ship-icon svg {
  display: block;
  width: 100%;
  height: 100%;
  transform-origin: center;
  transform: rotate(0deg) !important;
  pointer-events: none;
}

/* Add smooth scrolling to body */
:root {
  scroll-behavior: smooth;
}

/* Add custom marker styling */
.location-marker {
  display: flex;
  align-items: center;
  justify-content: center;
}

.location-marker svg {
  filter: drop-shadow(0 2px 2px rgba(0, 0, 0, 0.5));
}

/* Ensure content stays centered on wide screens */
/* @media (min-width: 601px) {
  #content-container {
    padding-left: calc((100vw - 600px) / 2);
    padding-right: calc((100vw - 600px) / 2);
  }
} */

/* Media queries for specific adjustments */
@media (max-width: 480px) {
  .voyage-stop {
    margin-bottom: 25vh;
    scroll-margin-top: 25vh;
  }

  .photos-container {
    grid-template-columns: repeat(2, 1fr);
  }

  .photo-placeholder {
    aspect-ratio: 1;
    font-size: 0.75rem;
  }

  #content-container {
    padding-top: 100px; /* Smaller padding on mobile */
  }
}

@media (max-width: 360px) {
  .photos-container {
    grid-template-columns: 1fr;
  }

  .voyage-stop {
    padding: 1.25rem;
  }
}

/* Add smooth ship movement */
.ship-icon {
  transition: transform 0.1s ease-out;
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
}

.ship-icon svg {
  display: block;
  width: 100%;
  height: 100%;
  transform-origin: center;
  transform: rotate(0deg) !important;
}

/* New animation for header from top */
@keyframes fadeInTop {
  from {
    opacity: 0;
    transform: translateY(-100%);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Update content container mobile styles */
@media (max-width: 768px) {
  #content-container {
    transform: none;
    width: 100%;
    padding-top: 80px;
  }

  #content-container > * {
    width: 75%;
    padding: 1rem;
    max-width: 100%;
  }

  .header-info {
    height: 80px;
    padding: 0.5rem;
  }

  .header-info h1 {
    font-size: 1rem;
  }

  .header-info p {
    font-size: 0.75rem;
  }

  .voyage-stop {
    margin-bottom: 15vh;
    padding: 1rem;
  }

  .photos-container {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
    margin-top: 1rem;
    padding-top: 1rem;
  }

  .photo-placeholder {
    min-height: 120px;
    min-width: auto;
  }
}

@media (max-width: 480px) {
  #content-container {
    padding-top: 70px;
  }

  .header-info {
    height: 70px;
  }

  .voyage-stop {
    margin-bottom: 10vh;
    border-radius: 12px;
  }

  .photos-container {
    grid-template-columns: repeat(2, 1fr);
  }

  .photo-placeholder {
    min-height: 100px;
  }
}

@media (max-width: 360px) {
  .photo-placeholder {
    min-height: 90px;
  }
}
</style>